import React, { useState, useMemo } from 'react';
import TextInput from '../TextInput/TextInput';
import type { Props as TextInputProps } from '../TextInput/TextInput';
import { handleDateInput } from './util';
import BaseInputIcon from '../BaseInput/BaseInputIcon';
import { ColorResolveOptions } from '../style/colors';

type DateFieldType = 'birthday';

export type DateInputProps = TextInputProps & {
  patternType?: 'short' | 'long';
  iconColor?: ColorResolveOptions;
  dateType?: DateFieldType;
};

export const DATE_PATTERN_SHORT = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])$/; // MM/DD
export const DATE_PATTERN_LONG =
  /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/; // MM/DD/YYYY

const DateInput: React.FC<DateInputProps> = (props) => {
  const {
    value = '',
    onChange,
    patternType = 'short',
    iconColor,
    dateType,
    ...commonProps
  } = props;

  const patterns: Record<'short' | 'long', RegExp> = {
    short: DATE_PATTERN_SHORT,
    long: DATE_PATTERN_LONG,
  };

  const [inputValue, setInputValue] = useState<string>(value);
  const [valid, setValid] = useState<boolean>(
    patterns[patternType].test(value)
  );

  const validInputIcon = useMemo(
    () =>
      valid ? (
        <BaseInputIcon
          aria-label="Valid"
          icon="checkmarkCircleInverted"
          color={
            iconColor || {
              type: 'theme',
              colorLegacy: 'Green/50',
              colorNext: 'success/default',
            }
          }
        />
      ) : null,
    [valid, iconColor]
  );

  const validateInput = (val: string): void => {
    if (patterns[patternType].test(val)) {
      setValid(true);
    } else {
      setValid(false);
    }
  };

  const onChangeFn = (newValue: string) => {
    const formattedValue = handleDateInput({
      previousValue: inputValue,
      newValue,
      format: patternType,
    });
    validateInput(formattedValue);
    setInputValue(formattedValue);
    if (onChange) onChange(formattedValue);
  };

  return (
    <TextInput
      {...commonProps}
      onChange={onChangeFn}
      value={inputValue}
      placeholder={patternType === 'short' ? 'MM/DD' : 'MM/DD/YYYY'}
      adornmentRight={validInputIcon}
      autoComplete={dateType === 'birthday' ? 'bday' : 'on'}
    />
  );
};

export default DateInput;
